// app/javascript/controllers/dae20/editor_pickers_controller.js
import { Controller } from "@hotwired/stimulus"

/**
 * Editor Pickers Controller
 *
 * Unified controller for all 3 pickers in the component editor:
 *   1. Template picker (component template / Lists::Value)
 *   2. Source picker (data source / Category)
 *   3. Item Template picker (data template / Category)
 *
 * Features:
 * - Toggle between normal center column content and any picker overlay
 * - Text input for direct code/id entry with human name resolution
 * - Click to select from visual grid
 * - Search/filter within each picker
 * - Source ↔ Item Template interconnection:
 *     • When source selected → matching item templates highlighted
 *     • When item template selected (and source empty) → auto-fill source from main_source_id
 */
export default class extends Controller {
  static targets = [
    "content",
    // Template picker
    "tplPicker", "tplInput", "tplHiddenId", "tplLabel", "tplSearch", "tplList",
    // Source picker
    "sourcePicker", "sourceInput", "sourceHiddenId", "sourceLabel", "sourceSearch", "sourceList",
    // Item Template picker
    "itPicker", "itInput", "itHiddenId", "itLabel", "itSearch", "itList"
  ]

  connect() {
    // Build lookup maps for template picker (code → id, id → {code, name})
    this.tplCodeToId = {}
    this.tplIdToInfo = {}
    const tplButtons = this.element.querySelectorAll("[data-template-id]")
    tplButtons.forEach(btn => {
      const id = btn.dataset.templateId
      const code = btn.dataset.templateCode
      const name = btn.dataset.templateName
      if (code && id) {
        this.tplCodeToId[code] = id
        this.tplIdToInfo[id] = { code, name }
      }
    })

    // Build lookup maps for source picker (code → id, id → {code, name})
    this.srcCodeToId = {}
    this.srcIdToInfo = {}
    const srcButtons = this.element.querySelectorAll("[data-source-id]")
    srcButtons.forEach(btn => {
      const id = btn.dataset.sourceId
      const code = btn.dataset.sourceCode
      const name = btn.dataset.sourceName
      if (id) {
        if (code) this.srcCodeToId[code] = id
        this.srcIdToInfo[id] = { code: code || "", name: name || "" }
      }
    })

    // Build lookup maps for item template picker (code → id, id → {code, name, mainSourceId})
    this.itCodeToId = {}
    this.itIdToInfo = {}
    const itButtons = this.element.querySelectorAll("[data-it-id]")
    itButtons.forEach(btn => {
      const id = btn.dataset.itId
      const name = btn.dataset.itName
      const code = btn.dataset.itCode || ""
      const mainSourceId = btn.dataset.itSource || ""
      if (id) {
        if (code) this.itCodeToId[code] = id
        this.itIdToInfo[id] = { code, name: name || "", mainSourceId }
      }
    })
  }

  // ═══════════════════════════════════════════════
  // TEMPLATE PICKER
  // ═══════════════════════════════════════════════

  toggleTemplate() {
    if (this.hasTplPickerTarget && !this.tplPickerTarget.classList.contains("hidden")) {
      this.closeTemplate()
    } else {
      this._closeAllPickers()
      this._openPicker(this.tplPickerTarget, this.hasTplSearchTarget ? this.tplSearchTarget : null)
    }
  }

  closeTemplate() {
    this._closePicker(this.tplPickerTarget)
  }

  selectTemplate(event) {
    const btn = event.currentTarget
    const id = btn.dataset.templateId
    const code = btn.dataset.templateCode
    const name = btn.dataset.templateName

    if (this.hasTplHiddenIdTarget) this.tplHiddenIdTarget.value = id
    if (this.hasTplInputTarget) this.tplInputTarget.value = code
    if (this.hasTplLabelTarget) {
      this.tplLabelTarget.textContent = name
      this.tplLabelTarget.title = name
      this.tplLabelTarget.classList.remove("hidden")
    }

    this._updatePickerSelected("[data-template-id]", "templateId", id, "blue")
    this.closeTemplate()
  }

  tplCodeChanged(event) {
    const code = event.target.value.trim()
    const id = this.tplCodeToId[code]

    if (id) {
      if (this.hasTplHiddenIdTarget) this.tplHiddenIdTarget.value = id
      const info = this.tplIdToInfo[id]
      if (this.hasTplLabelTarget && info) {
        this.tplLabelTarget.textContent = info.name
        this.tplLabelTarget.title = info.name
        this.tplLabelTarget.classList.remove("hidden")
      }
    } else {
      if (this.hasTplHiddenIdTarget) this.tplHiddenIdTarget.value = code
      if (this.hasTplLabelTarget) this.tplLabelTarget.classList.add("hidden")
    }
  }

  filterTemplate(event) {
    this._filterPicker(event, this.hasTplPickerTarget ? this.tplPickerTarget : null)
  }

  // ═══════════════════════════════════════════════
  // SOURCE PICKER
  // ═══════════════════════════════════════════════

  toggleSource() {
    if (this.hasSourcePickerTarget && !this.sourcePickerTarget.classList.contains("hidden")) {
      this.closeSource()
    } else {
      this._closeAllPickers()
      this._openPicker(this.sourcePickerTarget, this.hasSourceSearchTarget ? this.sourceSearchTarget : null)
    }
  }

  closeSource() {
    this._closePicker(this.sourcePickerTarget)
  }

  selectSource(event) {
    const btn = event.currentTarget
    const id = btn.dataset.sourceId
    const code = btn.dataset.sourceCode
    const name = btn.dataset.sourceName

    if (this.hasSourceHiddenIdTarget) this.sourceHiddenIdTarget.value = id
    if (this.hasSourceInputTarget) this.sourceInputTarget.value = code || id
    if (this.hasSourceLabelTarget) {
      this.sourceLabelTarget.textContent = name
      this.sourceLabelTarget.title = name
      this.sourceLabelTarget.classList.remove("hidden")
    }

    this._updatePickerSelected("[data-source-id]", "sourceId", id, "green")
    this.closeSource()
  }

  sourceCodeChanged(event) {
    const code = event.target.value.trim()
    const id = this.srcCodeToId[code]

    if (id) {
      if (this.hasSourceHiddenIdTarget) this.sourceHiddenIdTarget.value = id
      const info = this.srcIdToInfo[id]
      if (this.hasSourceLabelTarget && info) {
        this.sourceLabelTarget.textContent = info.name
        this.sourceLabelTarget.title = info.name
        this.sourceLabelTarget.classList.remove("hidden")
      }
    } else {
      // Maybe user typed a raw ID — try to find it
      const infoById = this.srcIdToInfo[code]
      if (infoById) {
        if (this.hasSourceHiddenIdTarget) this.sourceHiddenIdTarget.value = code
        if (this.hasSourceLabelTarget) {
          this.sourceLabelTarget.textContent = infoById.name
          this.sourceLabelTarget.title = infoById.name
          this.sourceLabelTarget.classList.remove("hidden")
        }
      } else {
        if (this.hasSourceHiddenIdTarget) this.sourceHiddenIdTarget.value = code
        if (this.hasSourceLabelTarget) this.sourceLabelTarget.classList.add("hidden")
      }
    }
  }

  filterSource(event) {
    this._filterPicker(event, this.hasSourcePickerTarget ? this.sourcePickerTarget : null)
  }

  // ═══════════════════════════════════════════════
  // ITEM TEMPLATE PICKER
  // ═══════════════════════════════════════════════

  toggleItemTemplate() {
    if (this.hasItPickerTarget && !this.itPickerTarget.classList.contains("hidden")) {
      this.closeItemTemplate()
    } else {
      this._closeAllPickers()
      this._openPicker(this.itPickerTarget, this.hasItSearchTarget ? this.itSearchTarget : null)
    }
  }

  closeItemTemplate() {
    this._closePicker(this.itPickerTarget)
  }

  selectItemTemplate(event) {
    const btn = event.currentTarget
    const id = btn.dataset.itId
    const name = btn.dataset.itName
    const code = btn.dataset.itCode || ""
    const mainSourceId = btn.dataset.itSource || ""

    if (this.hasItHiddenIdTarget) this.itHiddenIdTarget.value = id
    if (this.hasItInputTarget) this.itInputTarget.value = code || id
    if (this.hasItLabelTarget) {
      this.itLabelTarget.textContent = name
      this.itLabelTarget.title = name
      this.itLabelTarget.classList.remove("hidden")
    }

    this._updatePickerSelected("[data-it-id]", "itId", id, "purple")

    // ═══ Interconnection: auto-fill source if empty ═══
    if (mainSourceId && this.hasSourceHiddenIdTarget) {
      const currentSourceId = this.sourceHiddenIdTarget.value
      if (!currentSourceId || currentSourceId.trim() === "") {
        // Source is empty — auto-fill from item template's main_source_id
        const srcInfo = this.srcIdToInfo[mainSourceId]
        if (srcInfo) {
          this.sourceHiddenIdTarget.value = mainSourceId
          if (this.hasSourceInputTarget) {
            this.sourceInputTarget.value = srcInfo.code || mainSourceId
          }
          if (this.hasSourceLabelTarget) {
            this.sourceLabelTarget.textContent = srcInfo.name
            this.sourceLabelTarget.title = srcInfo.name
            this.sourceLabelTarget.classList.remove("hidden")
          }
          // Flash source input to indicate auto-fill
          this._flashElement(this.hasSourceInputTarget ? this.sourceInputTarget : null, "green")
        }
      }
    }

    this.closeItemTemplate()
  }

  itCodeChanged(event) {
    const val = event.target.value.trim()

    // Try code → id first
    const idFromCode = this.itCodeToId[val]
    if (idFromCode) {
      if (this.hasItHiddenIdTarget) this.itHiddenIdTarget.value = idFromCode
      const info = this.itIdToInfo[idFromCode]
      if (this.hasItLabelTarget && info) {
        this.itLabelTarget.textContent = info.name
        this.itLabelTarget.title = info.name
        this.itLabelTarget.classList.remove("hidden")
      }
      return
    }

    // Maybe user typed raw ID
    const infoById = this.itIdToInfo[val]
    if (infoById) {
      if (this.hasItHiddenIdTarget) this.itHiddenIdTarget.value = val
      if (this.hasItLabelTarget) {
        this.itLabelTarget.textContent = infoById.name
        this.itLabelTarget.title = infoById.name
        this.itLabelTarget.classList.remove("hidden")
      }
    } else {
      // Unknown — pass as-is
      if (this.hasItHiddenIdTarget) this.itHiddenIdTarget.value = val
      if (this.hasItLabelTarget) this.itLabelTarget.classList.add("hidden")
    }
  }

  filterItemTemplate(event) {
    this._filterPicker(event, this.hasItPickerTarget ? this.itPickerTarget : null)
  }

  // ═══════════════════════════════════════════════
  // PRIVATE HELPERS
  // ═══════════════════════════════════════════════

  _closeAllPickers() {
    const pickers = [
      this.hasTplPickerTarget ? this.tplPickerTarget : null,
      this.hasSourcePickerTarget ? this.sourcePickerTarget : null,
      this.hasItPickerTarget ? this.itPickerTarget : null
    ]
    pickers.forEach(p => {
      if (p) p.classList.add("hidden")
    })
  }

  _openPicker(pickerEl, searchEl) {
    if (!pickerEl) return
    this.contentTarget.classList.add("hidden")
    pickerEl.classList.remove("hidden")
    if (searchEl) {
      searchEl.value = ""
      searchEl.focus()
      this._showAllInPicker(pickerEl)
    }
  }

  _closePicker(pickerEl) {
    if (!pickerEl) return
    pickerEl.classList.add("hidden")
    this.contentTarget.classList.remove("hidden")
  }

  _filterPicker(event, pickerEl) {
    if (!pickerEl) return
    const query = (event.target.value || "").toLowerCase().trim()
    if (!query) {
      this._showAllInPicker(pickerEl)
      return
    }

    const cards = pickerEl.querySelectorAll("[data-searchable]")
    const groups = pickerEl.querySelectorAll("[data-picker-group]")

    cards.forEach(card => {
      const text = card.dataset.searchable || ""
      card.classList.toggle("hidden", !text.includes(query))
    })

    // Hide groups with no visible cards
    groups.forEach(group => {
      const visibleCards = group.querySelectorAll("[data-searchable]:not(.hidden)")
      group.classList.toggle("hidden", visibleCards.length === 0)
    })
  }

  _showAllInPicker(pickerEl) {
    if (!pickerEl) return
    pickerEl.querySelectorAll("[data-searchable]").forEach(el => el.classList.remove("hidden"))
    pickerEl.querySelectorAll("[data-picker-group]").forEach(el => el.classList.remove("hidden"))
  }

  // Color class maps — Tailwind JIT needs full class names at build time
  static COLOR_CLASSES = {
    blue:   { border: "border-blue-500",   bg: "bg-blue-50",   ring: "ring-blue-500",   badgeBg: "bg-blue-500",   flash: "ring-blue-400" },
    green:  { border: "border-green-500",  bg: "bg-green-50",  ring: "ring-green-500",  badgeBg: "bg-green-500",  flash: "ring-green-400" },
    purple: { border: "border-purple-500", bg: "bg-purple-50", ring: "ring-purple-500", badgeBg: "bg-purple-500", flash: "ring-purple-400" }
  }

  _updatePickerSelected(selector, dataAttr, selectedId, color) {
    const cc = this.constructor.COLOR_CLASSES[color] || this.constructor.COLOR_CLASSES.blue
    const allColorClasses = Object.values(this.constructor.COLOR_CLASSES).flatMap(c => [c.border, c.bg, c.ring])

    const buttons = this.element.querySelectorAll(selector)
    buttons.forEach(btn => {
      const isSelected = btn.dataset[dataAttr] === selectedId

      // Reset all
      btn.classList.remove(...allColorClasses, "ring-1")
      btn.classList.add("border-gray-200", "bg-white")

      // Remove old badge
      const badge = btn.querySelector("[data-selected-badge]")
      if (badge) badge.remove()

      if (isSelected) {
        btn.classList.add(cc.border, cc.bg, "ring-1", cc.ring)
        btn.classList.remove("border-gray-200", "bg-white")
        // Add badge
        const badgeEl = document.createElement("div")
        badgeEl.setAttribute("data-selected-badge", "")
        badgeEl.className = `absolute top-1 right-1 w-4 h-4 ${cc.badgeBg} rounded-full flex items-center justify-center`
        badgeEl.innerHTML = '<svg class="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/></svg>'
        btn.appendChild(badgeEl)
      }
    })
  }

  // Brief flash animation on an element (e.g., when auto-filling source)
  _flashElement(el, color) {
    if (!el) return
    const cc = this.constructor.COLOR_CLASSES[color] || this.constructor.COLOR_CLASSES.green
    el.classList.add("ring-2", cc.flash)
    setTimeout(() => {
      el.classList.remove("ring-2", cc.flash)
    }, 1500)
  }
}
